import {
  decodeVimeo,
  decodeYouTube,
  encodeVimeo,
  encodeYouTube,
  extrapolateYouTubeTime,
  isTrustedProviderOrigin,
  providerTargetOrigin,
  type PlaybackProvider,
  type PlayerEvent,
} from '@mlm/shared';
import {
  applyEvent,
  createSnapshotStore,
  type PlaybackAdapter,
  type PlayerSnapshot,
} from './adapter';

/** Stop knocking after this long: an ad blocker or a passthrough iframe never answers. */
const HANDSHAKE_TIMEOUT_MS = 15_000;
const YT_LISTEN_INTERVAL_MS = 250;
const VIMEO_PING_INTERVAL_MS = 500;
/** YouTube delivers time in irregular bursts; extrapolate between them. */
const YT_TICK_MS = 250;
/** No `playbackratechange` inside this window = the provider silently refused. */
const RATE_CONFIRM_MS = 2_000;

const YT_SUBSCRIPTIONS = ['onStateChange', 'onPlaybackRateChange', 'onError', 'onAutoplayBlocked'];
const VIMEO_SUBSCRIPTIONS = [
  'play',
  'playing',
  'pause',
  'ended',
  'timeupdate',
  'seeked',
  'durationchange',
  'playbackratechange',
  'bufferstart',
  'bufferend',
  'error',
];

type EmbedOptions = { provider: PlaybackProvider; src: string; protocolId: string };

/**
 * Provider-iframe half of the adapter, speaking each vendor's postMessage
 * protocol directly — no vendor SDK, matching the dependency-free precedent
 * set by the toast. It replaces the old one-shot 800 ms `listening` timer with
 * a real handshake: keep knocking until the player answers, then subscribe.
 *
 * A provider that never answers (ad blocker, a passthrough URL that is not
 * YouTube or Vimeo, a domain-locked Vimeo) settles at `unsupported` rather than
 * spinning forever — the host then shows provider chrome only, and the manual
 * "Mark complete" button remains the learner's way out.
 */
export function createEmbedAdapter(
  iframe: HTMLIFrameElement,
  { provider, src, protocolId }: EmbedOptions,
): PlaybackAdapter {
  const store = createSnapshotStore({
    status: provider === 'unknown' ? 'unsupported' : 'handshaking',
  });
  const targetOrigin = provider === 'youtube' ? providerTargetOrigin(src) : '*';

  let destroyed = false;
  let handshakeTimer: ReturnType<typeof setInterval> | null = null;
  let handshakeDeadline: ReturnType<typeof setTimeout> | null = null;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let rateProbe: ReturnType<typeof setTimeout> | null = null;
  let rateProbeResolve: ((ok: boolean) => void) | null = null;
  // YouTube reports a timestamped sample; between samples we advance it
  // ourselves with the same 1-second clamp the official widget API uses.
  let lastTimeSample: { currentTime: number; reportedAt?: number; rate?: number } | null = null;
  let pendingSeekTo: number | null = null;

  // Commands issued before the handshake completes are queued, last-wins per
  // kind, and flushed on ready — a learner who taps play during the handshake
  // must not lose the tap.
  const queue = new Map<string, () => void>();

  const post = (payload: unknown) => {
    const win = iframe.contentWindow;
    if (!win || destroyed) return;
    try {
      win.postMessage(payload, targetOrigin);
    } catch {
      /* a cross-origin frame that went away mid-flight */
    }
  };

  const send = (kind: string, fn: () => void) => {
    if (store.get().status === 'ready') fn();
    else if (store.get().status !== 'unsupported') queue.set(kind, fn);
  };

  const push = (event: PlayerEvent, extra: Partial<PlayerSnapshot> = {}) => {
    if (destroyed) return;
    const patch = applyEvent(store.get(), event);
    store.patch({ ...(patch ?? {}), ...extra });
    store.emit(event);
  };

  const stopHandshake = () => {
    if (handshakeTimer) clearInterval(handshakeTimer);
    if (handshakeDeadline) clearTimeout(handshakeDeadline);
    handshakeTimer = null;
    handshakeDeadline = null;
  };

  const onReady = () => {
    if (store.get().status === 'ready') return;
    stopHandshake();
    store.patch({ status: 'ready' });
    if (provider === 'youtube') {
      for (const name of YT_SUBSCRIPTIONS) {
        post(encodeYouTube({ kind: 'subscribe', event: name }, protocolId));
      }
    } else if (provider === 'vimeo') {
      for (const name of VIMEO_SUBSCRIPTIONS) post(encodeVimeo({ kind: 'subscribe', event: name }));
      post(encodeVimeo({ kind: 'getDuration' }));
    }
    for (const fn of queue.values()) fn();
    queue.clear();
    push({ type: 'ready' });
  };

  const handleEvents = (events: PlayerEvent[]) => {
    for (const event of events) {
      if (event.type === 'ready') {
        onReady();
        continue;
      }
      if (event.type === 'time') {
        lastTimeSample = {
          currentTime: event.currentTime,
          reportedAt: event.reportedAt,
          rate: event.rate,
        };
        // A committed seek has landed once the reported time reaches it; the
        // providers do not all emit a distinct `seeked`, and the heartbeat
        // policy keys its debounce on one.
        if (pendingSeekTo != null && Math.abs(event.currentTime - pendingSeekTo) < 1.5) {
          pendingSeekTo = null;
          push({ type: 'seeked', currentTime: event.currentTime });
        }
      }
      if (event.type === 'rate') {
        if (rateProbe) clearTimeout(rateProbe);
        rateProbe = null;
        rateProbeResolve?.(true);
        rateProbeResolve = null;
      }
      if (event.type === 'error' && String(event.code ?? '') === 'setPlaybackRate') {
        // A creator can disable speed control on a Vimeo video: hide the UI
        // rather than surface an error the learner cannot act on.
        if (rateProbe) clearTimeout(rateProbe);
        rateProbe = null;
        rateProbeResolve?.(false);
        rateProbeResolve = null;
        store.patch({ rateSupported: false, rates: [] });
        continue;
      }
      push(event);
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (destroyed) return;
    // Both checks matter: the origin proves who sent it, the source proves it
    // was OUR iframe and not another embed on the page.
    if (event.source !== iframe.contentWindow) return;
    if (!isTrustedProviderOrigin(provider, event.origin)) return;
    const events =
      provider === 'youtube' ? decodeYouTube(event.data, protocolId) : decodeVimeo(event.data);
    if (events) handleEvents(events);
  };

  const knock = () => {
    if (provider === 'youtube') post(encodeYouTube({ kind: 'listening' }, protocolId));
    else if (provider === 'vimeo') post(encodeVimeo({ kind: 'ping' }));
  };

  const startHandshake = () => {
    if (provider === 'unknown' || destroyed) return;
    stopHandshake();
    knock();
    handshakeTimer = setInterval(
      knock,
      provider === 'youtube' ? YT_LISTEN_INTERVAL_MS : VIMEO_PING_INTERVAL_MS,
    );
    handshakeDeadline = setTimeout(() => {
      stopHandshake();
      if (store.get().status !== 'ready') {
        store.patch({ status: 'unsupported' });
        queue.clear();
      }
    }, HANDSHAKE_TIMEOUT_MS);
  };

  // A provider iframe re-initialises on navigation; re-handshake when it does.
  const onIframeLoad = () => {
    if (store.get().status !== 'ready') startHandshake();
  };

  if (provider !== 'unknown') {
    window.addEventListener('message', onMessage);
    iframe.addEventListener('load', onIframeLoad);
    startHandshake();
    if (provider === 'youtube') {
      tickTimer = setInterval(() => {
        const snap = store.get();
        if (snap.state !== 'playing' || !lastTimeSample) return;
        const now = Date.now() / 1000;
        store.patch({
          currentTime: extrapolateYouTubeTime({ ...lastTimeSample, playing: true }, now),
        });
      }, YT_TICK_MS);
    }
  }

  return {
    async play() {
      send('play', () =>
        post(
          provider === 'youtube'
            ? encodeYouTube({ kind: 'play' }, protocolId)
            : encodeVimeo({ kind: 'play' }),
        ),
      );
    },
    pause() {
      send('pause', () =>
        post(
          provider === 'youtube'
            ? encodeYouTube({ kind: 'pause' }, protocolId)
            : encodeVimeo({ kind: 'pause' }),
        ),
      );
    },
    seek(seconds, opts) {
      const target = Math.max(0, seconds);
      if (!opts?.preview) pendingSeekTo = target;
      store.patch({ currentTime: target });
      lastTimeSample = {
        currentTime: target,
        reportedAt: Date.now() / 1000,
        rate: store.get().rate,
      };
      send('seek', () =>
        post(
          provider === 'youtube'
            ? encodeYouTube({ kind: 'seek', seconds: target, preview: !!opts?.preview }, protocolId)
            : encodeVimeo({ kind: 'seek', seconds: target, preview: !!opts?.preview }),
        ),
      );
    },
    setRate(rate) {
      if (!store.get().rateSupported) return Promise.resolve(false);
      return new Promise<boolean>((resolve) => {
        rateProbeResolve?.(false);
        rateProbeResolve = resolve;
        if (rateProbe) clearTimeout(rateProbe);
        // Silence is a refusal: Vimeo simply never fires playbackratechange on
        // a video whose owner turned speed control off.
        rateProbe = setTimeout(() => {
          rateProbe = null;
          rateProbeResolve = null;
          store.patch({ rateSupported: false, rates: [] });
          resolve(false);
        }, RATE_CONFIRM_MS);
        send('setRate', () =>
          post(
            provider === 'youtube'
              ? encodeYouTube({ kind: 'setRate', rate }, protocolId)
              : encodeVimeo({ kind: 'setRate', rate }),
          ),
        );
      });
    },
    setMuted(muted) {
      store.patch({ muted });
      send('mute', () =>
        post(
          provider === 'youtube'
            ? encodeYouTube({ kind: muted ? 'mute' : 'unmute' }, protocolId)
            : encodeVimeo({ kind: muted ? 'mute' : 'unmute' }),
        ),
      );
    },
    setVolume(volume01) {
      const volume = Math.min(1, Math.max(0, volume01));
      store.patch({ volume });
      send('volume', () =>
        post(
          provider === 'youtube'
            ? encodeYouTube({ kind: 'setVolume', volume01: volume }, protocolId)
            : encodeVimeo({ kind: 'setVolume', volume01: volume }),
        ),
      );
    },
    setCaptions() {
      // Provider chrome owns its own captions; we never re-host them.
    },
    getSnapshot: store.get,
    subscribe: store.subscribe,
    capabilities: {
      rate: provider !== 'unknown',
      volume: provider !== 'unknown',
      captions: false,
      fullscreen: 'none',
    },
    destroy() {
      destroyed = true;
      stopHandshake();
      if (tickTimer) clearInterval(tickTimer);
      if (rateProbe) clearTimeout(rateProbe);
      rateProbeResolve?.(false);
      rateProbeResolve = null;
      window.removeEventListener('message', onMessage);
      iframe.removeEventListener('load', onIframeLoad);
      queue.clear();
      store.clear();
    },
  };
}
